/**
 * Shared CSS for the Glass + Aurora design language. Injected once per
 * page via DashShell's <style> tag.
 *
 * Classes:
 *   .dash-root        — page-level light gradient background
 *   .aurora-bg        — fixed container for animated blobs
 *   .aurora-1/2/3     — pastel radial gradients drifting slowly
 *   .grain            — very subtle noise overlay
 *   .glass-card       — frosted white surface, lifts on hover
 *   .glass-card-flat  — same but no transform on hover
 *   .glass-pill       — small frosted pill (e.g. "Email verified" callout)
 *   .fade-up          — entrance animation
 */
export const dashStyles = `
.dash-root {
  background:
    radial-gradient(ellipse at top, #ffffff 0%, #f8fafc 50%, #f1f5f9 100%),
    #ffffff;
}

.aurora-bg {
  position: fixed;
  inset: 0;
  z-index: 0;
  overflow: hidden;
  pointer-events: none;
}

.aurora {
  position: absolute;
  border-radius: 50%;
  filter: blur(90px);
  will-change: transform;
}

.aurora-1 {
  width: 60vw; height: 60vw;
  top: -25vh; left: -10vw;
  background: radial-gradient(circle, rgba(243,156,18,0.42) 0%, transparent 65%);
  animation: drift1 22s ease-in-out infinite alternate;
}
.aurora-2 {
  width: 55vw; height: 55vw;
  top: 5vh; right: -15vw;
  background: radial-gradient(circle, rgba(125,211,252,0.45) 0%, transparent 65%);
  animation: drift2 28s ease-in-out infinite alternate;
}
.aurora-3 {
  width: 50vw; height: 50vw;
  bottom: -20vh; left: 25vw;
  background: radial-gradient(circle, rgba(252,165,165,0.32) 0%, transparent 65%);
  animation: drift3 32s ease-in-out infinite alternate;
}

@keyframes drift1 {
  0%   { transform: translate(0, 0) scale(1); }
  100% { transform: translate(8vw, 6vh) scale(1.08); }
}
@keyframes drift2 {
  0%   { transform: translate(0, 0) scale(1); }
  100% { transform: translate(-6vw, 4vh) scale(1.1); }
}
@keyframes drift3 {
  0%   { transform: translate(0, 0) scale(1); }
  100% { transform: translate(5vw, -5vh) scale(1.06); }
}

.grain {
  position: absolute;
  inset: 0;
  opacity: 0.025;
  mix-blend-mode: multiply;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E");
}

.glass-card {
  position: relative;
  background: linear-gradient(180deg, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.72) 100%);
  backdrop-filter: blur(22px) saturate(180%);
  -webkit-backdrop-filter: blur(22px) saturate(180%);
  border: 1px solid rgba(255,255,255,0.85);
  border-radius: 18px;
  box-shadow:
    0 1px 0 rgba(255,255,255,0.9) inset,
    0 1px 2px rgba(15,23,42,0.04),
    0 20px 45px -20px rgba(26,54,93,0.18);
  transition: transform .25s ease, box-shadow .25s ease, border-color .25s ease;
}

.glass-card:not(.glass-card-flat):hover {
  border-color: rgba(243,156,18,0.30);
  transform: translateY(-1px);
  box-shadow:
    0 1px 0 rgba(255,255,255,1) inset,
    0 4px 10px rgba(243,156,18,0.06),
    0 24px 55px -18px rgba(26,54,93,0.22);
}

.glass-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 999px;
  background: rgba(255,255,255,0.85);
  backdrop-filter: blur(22px) saturate(180%);
  border: 1px solid rgba(255,255,255,0.85);
  box-shadow: 0 1px 2px rgba(15,23,42,0.04);
  font-size: 12px;
}

@keyframes fadeUp {
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
}

.fade-up {
  animation: fadeUp .55s cubic-bezier(0.22, 1, 0.36, 1) both;
}
`;
